import { useEffect, useState } from "react";
import { LayoutDashboard, FileText, Receipt } from "lucide-react";
import logo from '@/assets/logo.svg';
import bigLogo from '@/assets/big-logo.svg';
import { supplierAuthService } from '@/features/proveedores/api/supplierAuthService';
import { getSupplierProfile } from '@/features/proveedores/api/supplierProfile.service';

interface SupplierSidebarProps {
  activeRoute: string;
  onNavigate: (route: string) => void;
}

const SupplierSidebar = ({ activeRoute, onNavigate }: SupplierSidebarProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [profileDisplayName, setProfileDisplayName] = useState<string | null>(
    null,
  );
  const supplierData = supplierAuthService.getSupplierData();
  const supplierDisplayName =
    profileDisplayName ||
    supplierData?.mainContactName ||
    supplierData?.legalName ||
    "Proveedor";

  useEffect(() => {
    let isMounted = true;

    const loadSupplierDisplayName = async () => {
      try {
        const profile = await getSupplierProfile();
        const name =
          profile?.general?.mainContactName?.trim() ||
          profile?.general?.legalName?.trim() ||
          null;

        if (isMounted) {
          setProfileDisplayName(name);
        }
      } catch {
        if (isMounted) {
          setProfileDisplayName(null);
        }
      }
    };

    loadSupplierDisplayName();

    return () => {
      isMounted = false;
    };
  }, []);

  const menuItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      route: "/proveedores/dashboard",
    },
    {
      id: "ordenes",
      label: "Órdenes de Compra",
      icon: Receipt,
      route: "/proveedores/ordenes",
    },
    {
      id: "expediente",
      label: "Mi expediente",
      icon: FileText,
      route: "/proveedores/expediente",
    },
  ];

  return (
    <div
      className={`${
        isHovered ? "w-64" : "w-20"
        // z-20: por encima del Header (z-10) y del contenido, pero por debajo de
        // los modales (z-50+) para que sus overlays sí cubran el sidebar.
      } bg-white border-r border-[#E5E7EB] flex flex-col transition-all duration-300 fixed h-screen z-20`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Logo */}
      <div
        className={`flex items-center justify-center px-4 relative z-20 bg-white ${
          isHovered ? "border-b border-r border-[#E5E7EB] h-20 pb-4" : "h-16 "
        }`}
      >
        <img
          src={isHovered ? bigLogo : logo}
          alt="Logo"
          className={`${isHovered ? "h-12 mt-6" : "h-8"} transition-all`}
        />
      </div>

      {/* Menu Items */}
      {/* overflow-x-hidden: con solo overflow-y-auto, el eje X pasa de `visible` a
          `auto` y el tooltip (left-full) generaba una barra de scroll horizontal
          cuando el menu esta colapsado. */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-3">
        <ul className="space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              activeRoute === item.route || activeRoute === item.id;

            return (
              <li key={item.id}>
                <button
                  onClick={() => onNavigate(item.route)}
                  aria-label={item.label}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative text-sm ${
                    isActive
                      ? "bg-[#C3CBEB] text-[#232F61] font-semibold"
                      : "text-[#404040] hover:bg-[#C3CBEB] hover:text-[#232F61]"
                  } ${!isHovered ? "justify-center" : ""}`}
                >
                  <Icon
                    className={`flex-shrink-0 w-5 h-5 transition-colors ${
                      isActive
                        ? "text-[#404040]"
                        : "text-[#232F61] group-hover:text-[#404040]"
                    }`}
                  />
                  {isHovered && (
                    <span className="font-medium truncate">{item.label}</span>
                  )}

                  {!isHovered && (
                    <span className="pointer-events-none absolute left-full ml-2 px-2 py-1 rounded-md bg-gray-900 text-white text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
                      {item.label}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User Info */}
      <div className="border-t border-gray-200 p-4">
        {isHovered && supplierData && (
          <div className="mb-3 px-2">
            <p className="text-xs font-semibold text-gray-900 truncate">
              {supplierDisplayName}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {supplierData?.legalName || supplierData?.mainContactName}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierSidebar;
